/**
 * 草图圆角/倒角尺寸指示器管理器。
 *
 * 说明：槽口尺寸已由真实尺寸聚合（`createSlotAutoDimensions` + 尺寸仓库）接管，
 * 本管理器只保留圆角/倒角的创建期装饰标注。
 */

import * as THREE from "three/webgpu";
import {CSS2DObject} from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import type {Viewer} from '../viewer/Viewer';

/** 尺寸线颜色。 */
const DIMENSION_COLOR = 0x000000;
/** 尺寸线不透明度。 */
const DIMENSION_OPACITY = 0.9;
/** 延伸线不透明度。 */
const EXTENSION_OPACITY = 0.7;
/** 标签文字颜色。 */
const LABEL_COLOR = '#111827';
/** 标签背景颜色。 */
const LABEL_BG = 'rgba(255, 255, 255, 0.92)';
/** 标签边框颜色。 */
const LABEL_BORDER = 'rgba(17, 24, 39, 0.35)';
/** 最小刻度线尺寸。 */
const MIN_TICK_SIZE = 0.0018;
/** 固定见证刻度尺寸。 */
const FIXED_WITNESS_TICK_SIZE = 0.0024;
/**
 * 圆角尺寸数据源。
 */
export interface FilletDimensionSource {
    /** 是否添加尺寸标注。 */
    addDimensions: boolean;
    /** 中心点。 */
    center: [number, number, number];
    /** 半径。 */
    radius: number;
    /** 起始角度。 */
    startAngle: number;
    /** 结束角度。 */
    endAngle: number;
    /** 是否顺时针。 */
    clockwise?: boolean;
    /** 局部 X 轴方向。 */
    xAxis: [number, number, number];
    /** 局部 Y 轴方向。 */
    yAxis: [number, number, number];
}

/**
 * 倒角尺寸数据源。
 */
export interface ChamferDimensionSource {
    /** 是否添加尺寸标注。 */
    addDimensions: boolean;
    /** 角点。 */
    corner: [number, number, number];
    /** 起点。 */
    start: [number, number, number];
    /** 终点。 */
    end: [number, number, number];
    /** 局部 X 轴方向。 */
    xAxis: [number, number, number];
    /** 局部 Y 轴方向。 */
    yAxis: [number, number, number];
}

/**
 * 草图尺寸管理器类。
 */
export class SketchDimensionManager {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;
    /** 场景引用。 */
    private scene: THREE.Scene;
    /** 圆角尺寸分组映射。 */
    private filletDimensionGroups: Map<string, THREE.Group> = new Map();
    /** 倒角尺寸分组映射。 */
    private chamferDimensionGroups: Map<string, THREE.Group> = new Map();

    /**
     * @param viewer Viewer 实例。
     */
    constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.scene = viewer.scene;
    }

    /**
     * 同步圆角尺寸标注。
     * @param arc 圆弧线对象。
     * @param source 圆角尺寸数据源。
     */
    syncFilletDimensions(arc: THREE.Line, source: FilletDimensionSource): void {
        this.removeFilletDimensions(arc);
        if (!source.addDimensions) {
            return;
        }

        const center = this.toVector3(source.center);
        const radius = Math.max(source.radius, 1e-6);
        const xAxis = this.toVector3(source.xAxis).normalize();
        const yAxis = this.toVector3(source.yAxis).normalize();
        const planeNormal = new THREE.Vector3().crossVectors(xAxis, yAxis).normalize();
        if (planeNormal.lengthSq() < 1e-12) {
            return;
        }
        if (!Number.isFinite(source.startAngle) || !Number.isFinite(source.endAngle)) {
            return;
        }

        const clockwise = Boolean(source.clockwise);
        const normalized = this.normalizeArcAngles(source.startAngle, source.endAngle, clockwise);
        const midAngle = this.getArcMidAngle(normalized.start, normalized.end, clockwise);
        const radial = this.directionFromAngle(midAngle, xAxis, yAxis);
        const tangent = this.getArcTangentDirection(midAngle, clockwise, xAxis, yAxis);
        const endpoint = center.clone().addScaledVector(radial, radius);

        const group = new THREE.Group();
        group.name = 'fillet-dimensions';
        this.addLinearDimension(group, {
            start: center,
            end: endpoint,
            planeNormal,
            labelText: `R${this.formatLength(radius)}`,
            labelOffset: tangent.clone().multiplyScalar(Math.max(radius * 0.08, 0.002)),
            showStartArrow: false,
            showStartWitness: false,
        });

        if (group.children.length === 0) {
            this.disposeGroup(group);
            return;
        }
        this.viewer.addObject(group, arc);
        this.filletDimensionGroups.set(arc.uuid, group);
    }

    /**
     * 移除圆角尺寸标注。
     * @param arc 圆弧线对象。
     */
    removeFilletDimensions(arc: THREE.Line): void {
        const existing = this.filletDimensionGroups.get(arc.uuid);
        if (!existing) {
            return;
        }
        if (existing.parent) {
            existing.parent.remove(existing);
        }
        this.disposeGroup(existing);
        this.filletDimensionGroups.delete(arc.uuid);
    }

    /**
     * 同步倒角尺寸标注。
     * @param line 倒角线对象。
     * @param source 倒角尺寸数据源。
     */
    syncChamferDimensions(line: THREE.Line, source: ChamferDimensionSource): void {
        this.removeChamferDimensions(line);
        if (!source.addDimensions) {
            return;
        }

        const corner = this.toVector3(source.corner);
        const start = this.toVector3(source.start);
        const end = this.toVector3(source.end);
        const xAxis = this.toVector3(source.xAxis).normalize();
        const yAxis = this.toVector3(source.yAxis).normalize();
        const planeNormal = new THREE.Vector3().crossVectors(xAxis, yAxis).normalize();
        if (planeNormal.lengthSq() < 1e-12) {
            return;
        }

        const d1 = start.clone().sub(corner);
        const d2 = end.clone().sub(corner);
        if (d1.lengthSq() < 1e-12 || d2.lengthSq() < 1e-12) {
            return;
        }

        const group = new THREE.Group();
        group.name = 'chamfer-dimensions';

        const tangent1 = new THREE.Vector3().crossVectors(planeNormal, d1).normalize();
        const tangent2 = new THREE.Vector3().crossVectors(planeNormal, d2).normalize();
        const dir1 = d1.clone().normalize();
        const dir2 = d2.clone().normalize();
        const sign1 = Math.sign(tangent1.dot(dir2)) || 1;
        const sign2 = Math.sign(tangent2.dot(dir1)) || 1;
        const outward1 = tangent1.clone().multiplyScalar(-sign1);
        const outward2 = tangent2.clone().multiplyScalar(-sign2);
        const offset1 = Math.max(d1.length() * 0.35, 0.0024);
        const offset2 = Math.max(d2.length() * 0.35, 0.0024);
        const dim1Start = corner.clone().addScaledVector(outward1, offset1);
        const dim1End = start.clone().addScaledVector(outward1, offset1);
        const dim2Start = corner.clone().addScaledVector(outward2, offset2);
        const dim2End = end.clone().addScaledVector(outward2, offset2);
        this.addLinearDimension(group, {
            start: dim1Start,
            end: dim1End,
            extensionStart: corner,
            extensionEnd: start,
            planeNormal,
            labelText: this.formatLength(d1.length()),
            labelOffset: outward1.clone().multiplyScalar(Math.max(d1.length() * 0.04, 0.0012)),
            showStartWitness: false,
            showEndWitness: false,
        });
        this.addLinearDimension(group, {
            start: dim2Start,
            end: dim2End,
            extensionStart: corner,
            extensionEnd: end,
            planeNormal,
            labelText: this.formatLength(d2.length()),
            labelOffset: outward2.clone().multiplyScalar(Math.max(d2.length() * 0.04, 0.0012)),
            showStartWitness: false,
            showEndWitness: false,
        });

        if (group.children.length === 0) {
            this.disposeGroup(group);
            return;
        }
        this.viewer.addObject(group, line);
        this.chamferDimensionGroups.set(line.uuid, group);
    }

    /**
     * 移除倒角尺寸标注。
     * @param line 倒角线对象。
     */
    removeChamferDimensions(line: THREE.Line): void {
        const existing = this.chamferDimensionGroups.get(line.uuid);
        if (!existing) {
            return;
        }
        if (existing.parent) {
            existing.parent.remove(existing);
        }
        this.disposeGroup(existing);
        this.chamferDimensionGroups.delete(line.uuid);
    }

    /**
     * 释放所有尺寸标注资源。
     */
    dispose(): void {
        // 清理圆角尺寸分组
        this.filletDimensionGroups.forEach((group) => {
            if (group.parent) {
                group.parent.remove(group);
            }
            this.disposeGroup(group);
        });
        this.filletDimensionGroups.clear();

        // 清理倒角尺寸分组
        this.chamferDimensionGroups.forEach((group) => {
            if (group.parent) {
                group.parent.remove(group);
            }
            this.disposeGroup(group);
        });
        this.chamferDimensionGroups.clear();

        // 清理场景中可能泄漏的分组
        const leakedGroups: THREE.Group[] = [];
        this.scene.traverse((object) => {
            if (
                object instanceof THREE.Group
                && (
                    object.name === 'fillet-dimensions'
                    || object.name === 'chamfer-dimensions'
                )
            ) {
                leakedGroups.push(object);
            }
        });
        leakedGroups.forEach((group) => {
            group.parent?.remove(group);
            this.disposeGroup(group);
        });
    }

    /**
     * 添加线性尺寸标注。
     * @param group 目标分组。
     * @param params 线性尺寸参数。
     */
    private addLinearDimension(
        group: THREE.Group,
        params: {
            start: THREE.Vector3;
            end: THREE.Vector3;
            extensionStart?: THREE.Vector3;
            extensionEnd?: THREE.Vector3;
            planeNormal: THREE.Vector3;
            labelText: string;
            labelOffset?: THREE.Vector3;
            showStartArrow?: boolean;
            showEndArrow?: boolean;
            showStartWitness?: boolean;
            showEndWitness?: boolean;
        }
    ): void {
        const {start, end, extensionStart, extensionEnd, planeNormal, labelText} = params;
        const axis = end.clone().sub(start);
        const length = axis.length();
        if (length < 1e-8) {
            return;
        }
        const axisDir = axis.clone().normalize();
        const witnessDir = new THREE.Vector3().crossVectors(planeNormal, axisDir).normalize();
        const witnessSize = Math.max(FIXED_WITNESS_TICK_SIZE, MIN_TICK_SIZE);

        // 延伸线
        if (extensionStart && extensionStart.distanceTo(start) > 1e-8) {
            group.add(this.createLine(extensionStart, start, true));
        }
        if (extensionEnd && extensionEnd.distanceTo(end) > 1e-8) {
            group.add(this.createLine(extensionEnd, end, true));
        }

        // 见证线
        if (witnessDir.lengthSq() > 1e-8) {
            if (params.showStartWitness !== false) {
                this.addWitnessLine(group, start, witnessDir, witnessSize);
            }
            if (params.showEndWitness !== false) {
                this.addWitnessLine(group, end, witnessDir, witnessSize);
            }
        }

        // 尺寸线
        group.add(this.createLine(start, end, false));

        // 箭头
        const arrowSize = Math.max(length * 0.08, 0.0015);
        if (params.showStartArrow !== false) {
            this.addArrow(group, start, axisDir.clone().multiplyScalar(-1), planeNormal, arrowSize);
        }
        if (params.showEndArrow !== false) {
            this.addArrow(group, end, axisDir, planeNormal, arrowSize);
        }

        // 标签
        const labelPos = start.clone().add(end).multiplyScalar(0.5);
        if (params.labelOffset && params.labelOffset.lengthSq() > 1e-12) {
            // 文本应紧贴尺寸箭头线中点，偏移量仅保留很小的避让空间
            const maxOffset = Math.max(length * 0.02, 0.0008);
            const offset = params.labelOffset.clone();
            if (offset.length() > maxOffset) {
                offset.setLength(maxOffset);
            }
            labelPos.add(offset);
        }
        group.add(this.createLabel(labelText, labelPos));
    }

    /**
     * 添加箭头。
     * @param group 目标分组。
     * @param position 箭头位置。
     * @param direction 箭头方向。
     * @param planeNormal 平面法向量。
     * @param size 箭头尺寸。
     */
    private addArrow(
        group: THREE.Group,
        position: THREE.Vector3,
        direction: THREE.Vector3,
        planeNormal: THREE.Vector3,
        size: number
    ): void {
        const dir = direction.clone();
        if (dir.lengthSq() < 1e-10) {
            return;
        }
        dir.normalize();
        const normal = planeNormal.clone();
        if (normal.lengthSq() < 1e-10) {
            return;
        }
        normal.normalize();

        const leg1 = dir.clone().applyAxisAngle(normal, THREE.MathUtils.degToRad(150)).multiplyScalar(size);
        const leg2 = dir.clone().applyAxisAngle(normal, THREE.MathUtils.degToRad(-150)).multiplyScalar(size);

        group.add(this.createLine(position, position.clone().add(leg1), false));
        group.add(this.createLine(position, position.clone().add(leg2), false));
    }

    /**
     * 添加见证线。
     * @param group 目标分组。
     * @param center 中心点。
     * @param direction 方向。
     * @param size 尺寸。
     */
    private addWitnessLine(
        group: THREE.Group,
        center: THREE.Vector3,
        direction: THREE.Vector3,
        size: number
    ): void {
        if (direction.lengthSq() < 1e-10 || size <= 0) {
            return;
        }
        const dir = direction.clone().normalize();
        const half = size / 2;
        const start = center.clone().addScaledVector(dir, -half);
        const end = center.clone().addScaledVector(dir, half);
        group.add(this.createLine(start, end, true));
    }

    /**
     * 创建折线。
     * @param points 点数组。
     * @param isAuxiliary 是否为辅助线。
     * @returns 线对象。
     */
    private createPolyline(points: THREE.Vector3[], isAuxiliary = false): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({
            color: DIMENSION_COLOR,
            linewidth: 1,
            transparent: true,
            opacity: isAuxiliary ? EXTENSION_OPACITY : DIMENSION_OPACITY,
        });
        const line = new THREE.Line(geometry, material);
        line.name = 'slot-dimension';
        return line;
    }

    /**
     * 创建线段。
     * @param start 起点。
     * @param end 终点。
     * @param isExtension 是否为延伸线。
     * @returns 线对象。
     */
    private createLine(start: THREE.Vector3, end: THREE.Vector3, isExtension: boolean): THREE.Line {
        return this.createPolyline([start, end], isExtension);
    }

    /**
     * 创建 CSS2D 标签。
     * @param text 标签文本。
     * @param position 位置。
     * @returns CSS2DObject。
     */
    private createLabel(text: string, position: THREE.Vector3): CSS2DObject {
        const element = document.createElement('div');
        element.className = 'slot-dimension-label';
        element.textContent = text;
        element.style.color = LABEL_COLOR;
        element.style.backgroundColor = LABEL_BG;
        element.style.border = `1px solid ${LABEL_BORDER}`;
        element.style.borderRadius = '3px';
        element.style.fontSize = '11px';
        element.style.fontWeight = '600';
        element.style.padding = '1px 4px';
        element.style.pointerEvents = 'none';
        element.style.userSelect = 'none';
        element.style.whiteSpace = 'nowrap';

        const label = new CSS2DObject(element);
        label.position.copy(position);
        label.name = 'slot-dimension-label';
        return label;
    }

    /**
     * 根据角度计算方向向量。
     * @param angle 角度（弧度）。
     * @param xAxis X 轴方向。
     * @param yAxis Y 轴方向。
     * @returns 方向向量。
     */
    private directionFromAngle(angle: number, xAxis: THREE.Vector3, yAxis: THREE.Vector3): THREE.Vector3 {
        return xAxis.clone()
            .multiplyScalar(Math.cos(angle))
            .addScaledVector(yAxis, Math.sin(angle))
            .normalize();
    }

    /**
     * 获取弧线切线方向。
     * @param angle 角度（弧度）。
     * @param clockwise 是否顺时针。
     * @param xAxis X 轴方向。
     * @param yAxis Y 轴方向。
     * @returns 切线方向向量。
     */
    private getArcTangentDirection(
        angle: number,
        clockwise: boolean,
        xAxis: THREE.Vector3,
        yAxis: THREE.Vector3
    ): THREE.Vector3 {
        if (clockwise) {
            return xAxis.clone()
                .multiplyScalar(Math.sin(angle))
                .addScaledVector(yAxis, -Math.cos(angle))
                .normalize();
        }
        return xAxis.clone()
            .multiplyScalar(-Math.sin(angle))
            .addScaledVector(yAxis, Math.cos(angle))
            .normalize();
    }

    /**
     * 获取弧线中间角度。
     * @param startAngle 起始角度。
     * @param endAngle 结束角度。
     * @param clockwise 是否顺时针。
     * @returns 中间角度。
     */
    private getArcMidAngle(startAngle: number, endAngle: number, clockwise: boolean): number {
        const normalized = this.normalizeArcAngles(startAngle, endAngle, clockwise);
        return clockwise
            ? normalized.start - normalized.angleRange * 0.5
            : normalized.start + normalized.angleRange * 0.5;
    }

    /**
     * 归一化弧线角度范围。
     * @param start 起始角度。
     * @param end 结束角度。
     * @param clockwise 是否顺时针。
     * @returns 归一化结果。
     */
    private normalizeArcAngles(start: number, end: number, clockwise: boolean): {
        start: number;
        end: number;
        angleRange: number;
    } {
        const twoPi = Math.PI * 2;
        const normalizedStart = start;
        let normalizedEnd = end;
        if (clockwise) {
            while (normalizedEnd > normalizedStart) {
                normalizedEnd -= twoPi;
            }
            return {
                start: normalizedStart,
                end: normalizedEnd,
                angleRange: normalizedStart - normalizedEnd,
            };
        }
        while (normalizedEnd < normalizedStart) {
            normalizedEnd += twoPi;
        }
        return {
            start: normalizedStart,
            end: normalizedEnd,
            angleRange: normalizedEnd - normalizedStart,
        };
    }

    /**
     * 将三元组转为 Vector3。
     * @param value 三元组。
     * @returns Vector3。
     */
    private toVector3(value: [number, number, number]): THREE.Vector3 {
        return new THREE.Vector3(value[0], value[1], value[2]);
    }

    /**
     * 格式化长度显示。
     * @param value 长度数值。
     * @returns 格式化字符串。
     */
    private formatLength(value: number): string {
        const safe = Number.isFinite(value) ? Math.max(value, 0) : 0;
        const roundedInteger = Math.round(safe);
        const stabilized = Math.abs(safe - roundedInteger) <= 1e-5
            ? roundedInteger
            : safe;
        return this.trimTrailingZeros(stabilized.toFixed(5));
    }

    /**
     * 去除尾随零。
     * @param value 数值字符串。
     * @returns 去除尾随零后的字符串。
     */
    private trimTrailingZeros(value: string): string {
        return value
            .replace(/(\.\d*?[1-9])0+$/u, '$1')
            .replace(/\.0+$/u, '')
            .replace(/\.$/u, '');
    }

    /**
     * 释放分组及其子对象资源。
     * @param group 目标分组。
     */
    private disposeGroup(group: THREE.Group): void {
        const children = [...group.children];
        children.forEach((child) => {
            group.remove(child);
            if (child instanceof CSS2DObject) {
                if (child.element?.parentElement) {
                    child.element.parentElement.removeChild(child.element);
                }
                return;
            }
            if (child instanceof THREE.Line || child instanceof THREE.LineSegments || child instanceof THREE.Sprite) {
                child.geometry?.dispose();
                const material = child.material;
                if (Array.isArray(material)) {
                    material.forEach((item) => item.dispose());
                } else {
                    material?.dispose();
                }
            }
        });
    }
}
